import { settingsApi } from "@/lib/api";
import { hasAnyPermission } from "@/lib/auth";

export interface MenuChildConfig {
  id: string;
  label: string;
  path: string;
}

export interface MenuItemConfig {
  id: string;
  label: string;
  path: string;
  icon: string;
  children?: MenuChildConfig[];
}

export const allMenuItems: MenuItemConfig[] = [
  { id: "landing", label: "Landing Page", path: "/landing", icon: "ri-pages-line" },
  { id: "dashboard", label: "Dashboard", path: "/", icon: "ri-dashboard-line" },
  { id: "projetos", label: "Projetos", path: "/projetos", icon: "ri-folder-chart-line" },
  { id: "cta", label: "CTA Arbóreo", path: "/cta", icon: "ri-plant-line" },
  { id: "mapa", label: "Mapa Arbóreo", path: "/mapa", icon: "ri-map-pin-line" },
  { id: "vistorias", label: "Vistorias", path: "/vistorias", icon: "ri-clipboard-line" },
  { id: "campanhas", label: "Campanhas de Campo", path: "/campanhas", icon: "ri-compass-3-line" },
  {
    id: "territorio",
    label: "Território",
    path: "/territorio",
    icon: "ri-map-2-line",
    children: [
      { id: "territorio.inspecoes", label: "Inspeções", path: "/territorio/inspecoes" },
      { id: "territorio.bairros", label: "Bairros", path: "/territorio/bairros" },
      { id: "territorio.quadras", label: "Quadras", path: "/territorio/quadras" },
      { id: "territorio.arvores", label: "Árvores", path: "/territorio/arvores" },
      { id: "territorio.indicadores", label: "Indicadores", path: "/territorio/indicadores" },
      { id: "territorio.console", label: "Console de Edição Territorial", path: "/territorio/console-webgis" },
    ],
  },
  { id: "ambiental", label: "Ambiental", path: "/ambiental", icon: "ri-landscape-line" },
  { id: "carbono", label: "Carbono", path: "/carbono", icon: "ri-leaf-line" },
  { id: "documentos", label: "Documentos e Fontes", path: "/documentos", icon: "ri-file-list-3-line" },
  { id: "partes", label: "Partes Envolvidas", path: "/partes", icon: "ri-team-line" },
  { id: "responsabilidades", label: "Responsabilidades", path: "/responsabilidades", icon: "ri-shield-check-line" },
  { id: "ladm", label: "Metodologia LADM", path: "/metodologia", icon: "ri-node-tree" },
  { id: "nuvem3d", label: "Nuvem de Pontos 3D", path: "/nuvem3d", icon: "ri-stack-line" },
  { id: "qualidade", label: "Validação e Qualidade", path: "/qualidade", icon: "ri-check-double-line" },
  { id: "operacional", label: "Fluxo Operacional", path: "/operacional", icon: "ri-git-branch-line" },
  { id: "relatorios", label: "Relatórios", path: "/relatorios", icon: "ri-bar-chart-box-line" },
  { id: "memorial", label: "Memorial Descritivo", path: "/memorial", icon: "ri-book-read-line" },
  { id: "piloto-sbv", label: "Área Piloto SBV", path: "/piloto/sbv", icon: "ri-flask-line" },
  { id: "configuracoes", label: "Configurações", path: "/configuracoes", icon: "ri-settings-3-line" },
];

const STORAGE_KEY = "floraiq_menu_visibility";

export type MenuVisibility = Record<string, boolean>;

function getDefaultVisibility(): MenuVisibility {
  const vis: MenuVisibility = {};
  for (const item of allMenuItems) {
    vis[item.id] = true;
    if (item.children) {
      for (const child of item.children) {
        vis[child.id] = true;
      }
    }
  }
  return vis;
}

export function loadMenuVisibility(): MenuVisibility {
  const defaults = getDefaultVisibility();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults;
    const saved = JSON.parse(raw) as MenuVisibility;
    for (const key of Object.keys(defaults)) {
      if (saved[key] === undefined) saved[key] = true;
    }
    return saved;
  } catch {
    return defaults;
  }
}

export function saveMenuVisibility(vis: MenuVisibility): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(vis));
}

export async function loadMenuVisibilityFromApi(): Promise<MenuVisibility> {
  const defaults = getDefaultVisibility();
  try {
    const res = await settingsApi.get("menu_visibility");
    const saved = (res.value ?? {}) as MenuVisibility;
    if (Object.keys(saved).length === 0) return defaults;
    for (const key of Object.keys(defaults)) {
      if (saved[key] === undefined) saved[key] = true;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
    return saved;
  } catch {
    return loadMenuVisibility();
  }
}

export async function saveMenuVisibilityToApi(vis: MenuVisibility): Promise<void> {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(vis));
  try {
    await settingsApi.put("menu_visibility", vis as Record<string, unknown>);
  } catch {
    // localStorage already saved as fallback
  }
}

const menuPermissions: Record<string, string[]> = {
  projetos: ["cta.read", "tree.view", "project.view"],
  cta: ["cta.read", "tree.view"],
  mapa: ["cta.read", "gis.read", "tree.view", "gis.view_map"],
  vistorias: ["cta.read", "inspection.view"],
  campanhas: ["cta.read", "campaign.view"],
  territorio: ["gis.read", "territory.view", "gis.view_map"],
  "territorio.inspecoes": ["gis.read", "territory.view"],
  "territorio.bairros": ["gis.read", "territory.view"],
  "territorio.quadras": ["gis.read", "territory.view"],
  "territorio.arvores": ["gis.read", "tree.view"],
  "territorio.indicadores": ["gis.read", "territory.view"],
  "territorio.console": ["gis.write", "gis.edit_map"],
  ambiental: ["cta.read", "tree.view"],
  carbono: ["cta.read", "tree.view"],
  documentos: ["documentos.read", "document.view"],
  partes: ["ladm.read", "report.view"],
  responsabilidades: ["ladm.read", "report.view"],
  ladm: ["ladm.read", "report.view"],
  nuvem3d: ["gis.read", "gis.view_map"],
  qualidade: ["qualidade.read", "quality.view"],
  operacional: ["cta.read", "tree.view"],
  relatorios: ["cta.read", "report.view"],
  memorial: ["cta.read", "report.view"],
  "piloto-sbv": ["gis.read", "territory.view"],
};

function hasMenuPermission(menuId: string): boolean {
  const required = menuPermissions[menuId];
  if (!required) return true;
  return hasAnyPermission(...required);
}

export function getVisibleMenuItems(vis: MenuVisibility): MenuItemConfig[] {
  return allMenuItems
    .filter((item) => vis[item.id] !== false && hasMenuPermission(item.id))
    .map((item) => {
      if (!item.children) return item;
      const visibleChildren = item.children.filter(
        (c) => vis[c.id] !== false && hasMenuPermission(c.id),
      );
      if (visibleChildren.length === 0) return { ...item, children: undefined };
      return { ...item, children: visibleChildren };
    });
}
